import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { campaignAPI } from '../services/api';

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    loadCampaigns();
  }, [page]);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const { data } = await campaignAPI.getAll(page);
      setCampaigns(data.content);
      setTotalPages(data.totalPages);
    } catch (err) {
      setError('Failed to load campaigns. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div style={styles.page}>
      <div style={styles.loading}>Loading campaigns...</div>
    </div>
  );

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.title}>Live Campaigns</h1>
        <p style={styles.subtitle}>Every campaign is verified. Every person is real.</p>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.grid}>
        {campaigns.map(campaign => (
          <CampaignCard key={campaign.id} campaign={campaign} />
        ))}
      </div>

      {campaigns.length === 0 && !loading && (
        <div style={styles.empty}>
          <p>No active campaigns right now.</p>
          <Link to="/create-campaign" style={styles.btnPrimary}>
            Start the first campaign →
          </Link>
        </div>
      )}

      {totalPages > 1 && (
        <div style={styles.pagination}>
          <button
            style={{ ...styles.pageBtn, opacity: page === 0 ? 0.4 : 1 }}
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}>
            ← Previous
          </button>
          <span style={styles.pageInfo}>Page {page + 1} of {totalPages}</span>
          <button
            style={{ ...styles.pageBtn, opacity: page >= totalPages - 1 ? 0.4 : 1 }}
            onClick={() => setPage(p => p + 1)}
            disabled={page >= totalPages - 1}>
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

function CampaignCard({ campaign }) {
  const pct = Math.min(campaign.progressPercent, 100);
  const daysLeft = Math.max(0, Math.ceil(
    (new Date(campaign.deadline) - new Date()) / (1000 * 60 * 60 * 24)
  ));

  return (
    <div style={styles.card}>
      <div style={styles.cardHead}>
        <div style={styles.avatar}>{campaign.creatorName?.charAt(0) || 'U'}</div>
        <div>
          <div style={styles.creatorName}>{campaign.creatorName}</div>
          <div style={styles.role}>{campaign.formerRole} · {campaign.formerCompany}</div>
        </div>
        <div style={styles.verified}>✓</div>
      </div>
      <h3 style={styles.campTitle}>{campaign.title}</h3>
      <p style={styles.story}>{campaign.story?.substring(0, 130)}...</p>
      <div style={styles.progTrack}>
        <div style={{ ...styles.progFill, width: `${pct}%` }} />
      </div>
      <div style={styles.meta}>
        <span style={styles.raised}>{campaign.currency} {campaign.raisedAmount?.toLocaleString()}</span>
        <span style={styles.pct}>{pct}%</span>
      </div>
      <div style={styles.metaRow}>
        <span style={styles.goal}>Goal: {campaign.currency} {campaign.goalAmount?.toLocaleString()}</span>
        <span style={styles.days}>{daysLeft} days left</span>
      </div>
      <Link to={`/campaigns/${campaign.id}`} style={styles.donateBtn}>
        Donate now →
      </Link>
    </div>
  );
}

const styles = {
  page: { maxWidth: 1160, margin: '0 auto', padding: '4rem 2rem' },
  header: { textAlign: 'center', marginBottom: '3rem' },
  title: { fontFamily: 'Fraunces, serif', fontSize: '2.5rem', fontWeight: 600, marginBottom: '0.5rem', color: '#1c1814' },
  subtitle: { color: '#7a6f65', fontSize: '1rem' },
  loading: { textAlign: 'center', padding: '4rem', color: '#7a6f65' },
  error: { background: '#fdf0ee', color: '#c0392b', padding: '1rem', borderRadius: 8, marginBottom: '2rem', textAlign: 'center' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.5rem' },
  empty: { textAlign: 'center', padding: '4rem', color: '#7a6f65' },
  card: { background: '#fff', border: '1.5px solid #e2dbd0', borderRadius: 16, padding: '1.5rem', transition: 'all 0.2s' },
  cardHead: { display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' },
  avatar: { width: 42, height: 42, borderRadius: '50%', background: '#fde8e4', color: '#c0392b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: '1rem', flexShrink: 0 },
  creatorName: { fontWeight: 600, fontSize: '0.9rem', color: '#1c1814' },
  role: { fontSize: '0.75rem', color: '#7a6f65' },
  verified: { marginLeft: 'auto', width: 24, height: 24, borderRadius: '50%', background: '#edf7f2', color: '#1e6f3e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 700, border: '1px solid rgba(30,111,62,0.2)' },
  campTitle: { fontSize: '1rem', fontWeight: 600, marginBottom: '0.65rem', color: '#1c1814' },
  story: { fontSize: '0.82rem', color: '#7a6f65', lineHeight: 1.7, marginBottom: '1.25rem', fontStyle: 'italic' },
  progTrack: { background: '#f4f0e8', borderRadius: 100, height: 8, overflow: 'hidden', marginBottom: '0.5rem' },
  progFill: { height: '100%', background: 'linear-gradient(90deg, #1e6f3e, #27ae60)', borderRadius: 100, transition: 'width 0.8s ease' },
  meta: { display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', marginBottom: '0.3rem' },
  raised: { fontWeight: 600, color: '#1c1814' },
  pct: { fontWeight: 600, color: '#1e6f3e' },
  metaRow: { display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#7a6f65', marginBottom: '1.25rem' },
  goal: {}, days: {},
  donateBtn: { display: 'block', background: '#1c1814', color: '#fff', textAlign: 'center', padding: '0.75rem', borderRadius: 10, textDecoration: 'none', fontSize: '0.875rem', fontWeight: 600, transition: 'all 0.2s' },
  pagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1.5rem', marginTop: '3rem' },
  pageBtn: { background: '#1c1814', color: '#fff', border: 'none', padding: '0.65rem 1.5rem', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' },
  pageInfo: { color: '#7a6f65', fontSize: '0.875rem' },
  btnPrimary: { display: 'inline-block', background: '#c0392b', color: '#fff', padding: '0.75rem 1.75rem', borderRadius: 10, textDecoration: 'none', fontWeight: 600, marginTop: '1rem' },
};
